// Hardcoded constants — change via release.
// Source of truth for: teams, pods, Edge pillars, fiscal calendar.

import type { Team, EdgePillar, Quarter, Status } from "./types";

// ----- Status labels -----
// IMPORTANT: keep STATUS_KEYS in sync with the Status union in types.ts.
// Used as the runtime source of truth for status iteration (modal dropdown, filter chips).
// If you add a new status: update Status in types.ts, then add it here.
export const STATUS_KEYS = ["draft", "not-started", "in-progress", "on-hold", "shipped", "canceled"] as const satisfies readonly Status[];

// Initiative description (wire name `tldr`): one or two sentences on what the
// work is and the outcome it's for. Required on create, optional on edit so
// historical rows still save. 240 chars fits two real sentences and keeps the
// list-view subtitle and MCP output tidy.
export const DESCRIPTION_MAX = 240;
// The live counter appears only once the writer is within this many chars.
export const DESCRIPTION_COUNTER_AT = DESCRIPTION_MAX - 40;
// One question, asked identically on create and on edit (approved copy, 2026-09-09).
export const DESCRIPTION_PLACEHOLDER = "What is this initiative, and what does it change for the business?";

// Lennar fiscal calendar
// Q1 = Dec / Jan / Feb
// Q2 = Mar / Apr / May
// Q3 = Jun / Jul / Aug
// Q4 = Sep / Oct / Nov
//
// The roadmap spans multiple fiscal years; the timeline shows a rolling
// 12-month window that steps by month (see rollingWindow). Quarter ids are
// year-qualified ("FY27-Q1") so they stay unambiguous across years. Legacy
// rows in the DB carry plain "Q1".."Q4" — normalizeQuarterId() reads those as
// FY26 (the only year that existed when they were written). No DB migration:
// targetQ is TEXT, new writes are qualified.
//
// TO ADD FY29: append it to FISCAL_YEARS. Everything else derives. Keep at
// least a year of runway past today — the timeline can't step past the end.
export const FISCAL_YEARS: { fy: string; startCalYear: number }[] = [
  { fy: "FY26", startCalYear: 2025 }, // Dec 2025 – Nov 2026
  { fy: "FY27", startCalYear: 2026 }, // Dec 2026 – Nov 2027
  { fy: "FY28", startCalYear: 2027 }, // Dec 2027 – Nov 2028
];
const LEGACY_FY = "FY26"; // plain "Q1".."Q4" ids predate the multi-FY calendar

function buildQuarters(fy: string, startCalYear: number): Quarter[] {
  const y = startCalYear;
  return [
    { id: `${fy}-Q1`, label: "Q1", start: new Date(y, 11, 1), end: new Date(y + 1, 2, 1), months: ["Dec", "Jan", "Feb"], year: fy },
    { id: `${fy}-Q2`, label: "Q2", start: new Date(y + 1, 2, 1), end: new Date(y + 1, 5, 1), months: ["Mar", "Apr", "May"], year: fy },
    { id: `${fy}-Q3`, label: "Q3", start: new Date(y + 1, 5, 1), end: new Date(y + 1, 8, 1), months: ["Jun", "Jul", "Aug"], year: fy },
    { id: `${fy}-Q4`, label: "Q4", start: new Date(y + 1, 8, 1), end: new Date(y + 1, 11, 1), months: ["Sep", "Oct", "Nov"], year: fy },
  ];
}

// All quarters across all fiscal years, chronological.
export const QUARTERS: Quarter[] = FISCAL_YEARS.flatMap(({ fy, startCalYear }) => buildQuarters(fy, startCalYear));

// Precomputed per-FY lookups — fyWindow/quartersOf sit under every bar render
// and every pointer-move frame during drags, so they must not re-filter
// QUARTERS on each call.
const QUARTERS_BY_FY = new Map<string, Quarter[]>(
  FISCAL_YEARS.map(({ fy }) => [fy, QUARTERS.filter(q => q.year === fy)])
);
const WINDOW_BY_FY = new Map<string, { start: Date; end: Date }>(
  FISCAL_YEARS.map(({ fy }) => {
    const qs = QUARTERS_BY_FY.get(fy)!;
    return [fy, { start: qs[0].start, end: qs[qs.length - 1].end }];
  })
);
const FALLBACK_WINDOW = WINDOW_BY_FY.get(FISCAL_YEARS[0].fy)!;

export function quartersOf(fy: string): Quarter[] {
  return QUARTERS_BY_FY.get(fy) ?? [];
}

// Calendar window of one fiscal year (the timeline's visible axis).
export function fyWindow(fy: string): { start: Date; end: Date } {
  return WINDOW_BY_FY.get(fy) ?? FALLBACK_WINDOW;
}

// Plain legacy ids ("Q1".."Q4") read as FY26; qualified ids pass through.
export function normalizeQuarterId(qId: string): string {
  return /^Q[1-4]$/.test(qId) ? `${LEGACY_FY}-${qId}` : qId;
}

// "FY27-Q2" -> "FY27 Q2" for display; legacy "Q2" -> "FY26 Q2".
export function quarterLabel(qId: string): string {
  const q = QUARTERS.find(x => x.id === normalizeQuarterId(qId));
  return q ? `${q.year} ${q.label}` : qId;
}

export function fiscalYearForDate(d: Date): string | undefined {
  return QUARTERS.find(x => d >= x.start && d < x.end)?.year;
}

export const TEAMS: Team[] = [
  {
    id: "lennarcom",
    name: "Lennar.com",
    pods: [
      { id: "search-personalization", name: "Search & Personalization" },
      { id: "core-cms", name: "Core & CMS" },
      { id: "growth-investor", name: "Growth & Investor Marketplace" },
      { id: "digital-buying-selling", name: "Digital Buying & Selling" },
      { id: "lennarcom-engineering", name: "Engineering" },
    ],
  },
  {
    id: "sales",
    name: "Sales Experience",
    pods: [
      { id: "isc-experience", name: "ISC Experience" },
      { id: "aisc", name: "AISC" },
      { id: "nhc-experience", name: "NHC Experience" },
      { id: "lifecycle-marketing", name: "Lifecycle Marketing" },
      { id: "sales-platform", name: "Platform" },
      { id: "quality-engineering", name: "Quality Engineering" },
    ],
  },
  {
    id: "cx",
    name: "Customer Experience",
    pods: [
      { id: "road-to-close", name: "Road to Close" },
      { id: "customer-mobile-app", name: "Customer Mobile App" },
      { id: "homeowner-experience", name: "Homeowner Experience" },
    ],
  },
  {
    // Shared function serving every team (closest to Lennar.com). No pods.
    id: "ux-research",
    name: "UX Research",
    pods: [],
  },
  {
    id: "internal-ops",
    name: "Internal Ops",
    pods: [
      { id: "creative-hub", name: "Creative Hub" },
    ],
  },
];

// NAMING CONVENTION: user-facing copy says "tag" everywhere. Internal code
// still calls them "pillars" (EdgePillar, pillarIds, .pillar-chip CSS) from
// when the only tags were the Edge objectives. Don't rename until you're
// ready for a multi-file refactor with no user-visible benefit.
//
// The current tag vocabulary. Each tag is offered to whole `teams` and/or
// specific `pods`; UX Research supports every team so it gets them all;
// Internal Ops has none. The API has no notion of scoped
// tags — these ids just have to exist in the tags table (POST /api/tags) or
// saves are rejected as unknown tag ids.
export const TAGS: EdgePillar[] = [
  { id: "unify-cx", name: "Unify and simplify the customer experience", short: "Unify and simplify CX", teams: ["lennarcom", "sales", "cx", "ux-research"] },
  { id: "eng-excellence", name: "Engineering Excellence", short: "Engineering Excellence", teams: ["lennarcom", "sales", "cx", "ux-research"] },
  { id: "quality-traffic", name: "Drive quality traffic", short: "Drive quality traffic", teams: ["lennarcom", "sales", "ux-research"] },
  { id: "appts-to-contracts", name: "Appointments → Customer contracts", short: "Appointments → contracts", teams: ["lennarcom", "sales", "ux-research"] },
  { id: "lim-sales", name: "Lennar Investor Marketplace Driven Sales (Total and Direct)", short: "LIM-driven sales", teams: ["lennarcom", "sales", "ux-research"] },
  { id: "cost-to-close", name: "Reduce cost to close", short: "Reduce cost to close", teams: ["ux-research"], pods: ["road-to-close", "customer-mobile-app"] },
  { id: "predictable-closing", name: "Make closing predictable", short: "Predictable closing", teams: ["ux-research"], pods: ["road-to-close", "customer-mobile-app"] },
  { id: "warranty-cost", name: "Reduce warranty cost to serve", short: "Warranty cost to serve", teams: ["ux-research"], pods: ["customer-mobile-app", "homeowner-experience"] },
  { id: "contact-resolution", name: "Decrease time to contact and resolution", short: "Time to resolution", teams: ["ux-research"], pods: ["customer-mobile-app", "homeowner-experience"] },
  { id: "customer-for-life", name: "Drive customer for life", short: "Customer for life", teams: ["cx", "ux-research"] },
];

// Retired tags. No longer offered in pickers, but initiatives already tagged
// keep them: they still display, filter, and can be unchecked.
//
// The 7 FY26 Edge Strategy objectives.
export const EDGE_PILLARS: EdgePillar[] = [
  { id: "p1", num: 1, name: "Increase quality traffic per community", short: "Quality traffic", retired: true },
  { id: "p2", num: 2, name: "Reduce Realtor spend", short: "Reduce Realtor spend", retired: true },
  { id: "p3", num: 3, name: "Improve sales program execution", short: "Sales execution", retired: true },
  { id: "p4", num: 4, name: "Scale Investor Marketplace", short: "Investor Marketplace", retired: true },
  { id: "p5", num: 5, name: "Seamless financial engagement", short: "Financial engagement", retired: true },
  { id: "p6", num: 6, name: "Optimize pricing, premiums and incentives", short: "Pricing & incentives", retired: true },
  { id: "p7", num: 7, name: "Simplify organizational structure and reduce overhead", short: "Org simplification", retired: true },
];

// Customer Experience's former tag set.
export const CX_TAGS: EdgePillar[] = [
  { id: "cx1", name: "Associate efficiency", short: "Associate efficiency", retired: true },
  { id: "cx2", name: "Standardized closing process", short: "Standardized closing process", retired: true },
  { id: "cx3", name: "Platform consolidation", short: "Platform consolidation", retired: true },
  { id: "cx4", name: "Customer experience", short: "Customer experience", retired: true },
  { id: "cx5", name: "Warranty", short: "Warranty", retired: true },
  { id: "cx6", name: "Homeowner Experience", short: "Homeowner Experience", retired: true },
];

// Every known tag — for displaying and filtering, regardless of team.
export const ALL_TAGS: EdgePillar[] = [...TAGS, ...EDGE_PILLARS, ...CX_TAGS];

// The tags an initiative on this team (and pod) may pick from.
export function tagsForTeam(teamId: string | undefined, podId?: string): EdgePillar[] {
  return TAGS.filter(t =>
    (!!teamId && !!t.teams?.includes(teamId)) || (!!podId && !!t.pods?.includes(podId)));
}

// The picker list: the team's tags, plus any tag the initiative already
// carries from outside that set (a retired tag, or one from another team)
// so it stays visible and can be unchecked rather than silently kept or dropped.
export function tagPickerOptions(teamId: string | undefined, podId: string | undefined, selected: string[]): EdgePillar[] {
  const allowed = tagsForTeam(teamId, podId);
  const extras = ALL_TAGS.filter(t => selected.includes(t.id) && !allowed.includes(t));
  return [...allowed, ...extras];
}

// Changing team or pod drops current tags the new home can't use. Retired
// tags are left alone — they come off only when someone unchecks them.
export function pruneTagsForTeam(teamId: string | undefined, podId: string | undefined, selected: string[]): string[] {
  const allowed = new Set(tagsForTeam(teamId, podId).map(t => t.id));
  const retired = new Set(ALL_TAGS.filter(t => t.retired).map(t => t.id));
  return selected.filter(id => allowed.has(id) || retired.has(id));
}

// "01"–"07" for Edge objectives; null for unnumbered tags.
export function tagNum(t: EdgePillar): string | null {
  return t.num ? String(t.num).padStart(2, "0") : null;
}

export const STATUS_LABELS: Record<Status, string> = {
  "draft": "Draft",
  "not-started": "Not Started",
  "in-progress": "In Progress",
  "on-hold": "On Hold",
  "shipped": "Shipped",
  "canceled": "Canceled",
};

// The real current date (midnight local) — drives the today line and the
// timeline's opening quarter. (Was a frozen prototype anchor until the
// FY27 extension made "which year are we in" matter.)
const NOW = new Date();
export const TODAY = new Date(NOW.getFullYear(), NOW.getMonth(), NOW.getDate());

// The fiscal year the timeline opens on: the one containing today, else the
// LAST defined — if the calendar has run past every configured FY (nobody
// appended the next year yet), opening on the most recent year beats
// silently rewinding to the oldest.
export const DEFAULT_FY: string = fiscalYearForDate(TODAY) ?? FISCAL_YEARS[FISCAL_YEARS.length - 1].fy;

// ----- Rolling timeline window -----
// The timeline shows WINDOW_MONTHS months (four whole fiscal quarters),
// stepped a quarter at a time. It opens on the current fiscal quarter, and the
// start is clamped so the whole window stays inside the configured calendar.
export const WINDOW_MONTHS = 12;

export function addMonths(d: Date, n: number): Date {
  return new Date(d.getFullYear(), d.getMonth() + n, 1);
}

export const WINDOW_START_MIN: Date = QUARTERS[0].start;
export const WINDOW_START_MAX: Date = addMonths(QUARTERS[QUARTERS.length - 1].end, -WINDOW_MONTHS);

export function clampWindowStart(d: Date): Date {
  // Snap to the start of d's quarter: the window never opens mid-quarter.
  const m = QUARTERS.find(q => d >= q.start && d < q.end)?.start ?? addMonths(d, 0);
  return m < WINDOW_START_MIN ? WINDOW_START_MIN : m > WINDOW_START_MAX ? WINDOW_START_MAX : m;
}

export const DEFAULT_WINDOW_START: Date = clampWindowStart(
  QUARTERS.find(q => TODAY >= q.start && TODAY < q.end)?.start ?? TODAY
);

export function rollingWindow(start: Date): TimeWindow {
  return { start, end: addMonths(start, WINDOW_MONTHS) };
}

export function sameMonth(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth();
}

// URL form of a window start: "2026-09". parseMonthParam returns undefined
// for anything malformed or outside the configured calendar, so the caller
// falls back to the default rather than jumping to a clamped edge.
export function monthParam(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}
export function parseMonthParam(s: string | null): Date | undefined {
  const m = s?.match(/^(\d{4})-(\d{2})$/);
  if (!m) return undefined;
  const month = Number(m[2]);
  if (month < 1 || month > 12) return undefined;
  // new Date(y, …) maps years 0–99 to 1900–1999 ("0026" → 1926); setFullYear doesn't.
  const d = new Date(2000, month - 1, 1);
  d.setFullYear(Number(m[1]));
  return d >= QUARTERS[0].start && d < QUARTERS[QUARTERS.length - 1].end ? d : undefined;
}

// "Sep 2026" — the window-range readout.
export function fmtMonthYear(d: Date): string {
  return d.toLocaleDateString(undefined, { month: "short", year: "numeric" });
}

// The fiscal quarters a window overlaps, each with the month labels it
// contributes and each month's length in days. A window that starts
// mid-quarter opens on a partial quarter (e.g. just "Nov" of Q4). The header
// sizes columns by days — bars are placed by day, so equal-width months
// would drift off the bars by a few pixels.
export function quarterSegments(win: TimeWindow): { q: Quarter; months: string[]; days: number[] }[] {
  return QUARTERS
    .filter(q => q.end > win.start && q.start < win.end)
    .map(q => {
      const idx = q.months.map((_, i) => i).filter(i => {
        const m = addMonths(q.start, i);
        return m >= win.start && m < win.end;
      });
      return {
        q,
        months: idx.map(i => q.months[i]),
        days: idx.map(i => new Date(q.start.getFullYear(), q.start.getMonth() + i + 1, 0).getDate()),
      };
    });
}

// Avatar colors — low-saturation cool hues (Gallery White: no golds/tans/greiges)
export const AVATAR_COLORS = [
  "#5E7E89", "#6B8AA3", "#7B9B8E", "#7E8C5F", "#6D7A99", "#877E93",
  "#7B8794", "#9A9A9F", "#7C8AA0", "#5F6977", "#8E9A8C", "#98A0AB",
];

// ---------- helpers ----------

export function quarterForDate(d: Date): string | undefined {
  const q = QUARTERS.find(x => d >= x.start && d < x.end);
  return q?.id; // year-qualified ("FY27-Q1")
}

// The timeline's visible axis — a full fiscal year normally, a single
// quarter when zoomed. All axis math is window-based so zoom is free.
export type TimeWindow = { start: Date; end: Date };

// Position of a date on the visible axis, as a % of the window. Clamped.
export function pctOfWindow(d: Date, win: TimeWindow): number {
  const total = win.end.getTime() - win.start.getTime();
  const at = d.getTime() - win.start.getTime();
  return Math.max(0, Math.min(100, (at / total) * 100));
}

// Unclamped variant — layout uses it to know a bar truly extends past the
// visible window (the clamped one can't tell "at the edge" from "beyond it").
export function pctOfWindowRaw(d: Date, win: TimeWindow): number {
  const total = win.end.getTime() - win.start.getTime();
  return ((d.getTime() - win.start.getTime()) / total) * 100;
}

export function toDate(arr?: [number, number, number]): Date | undefined {
  if (!arr) return undefined;
  return new Date(arr[0], arr[1], arr[2]);
}

// Local-timezone YYYY-MM-DD for <input type="date"> — toISOString() converts
// to UTC first, which shifts the calendar day for anyone east of Greenwich.
export function localISO(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

// <input type="date"> value ("YYYY-MM-DD") -> local [year, monthIdx, day].
export function isoToTuple(iso: string): [number, number, number] {
  const d = new Date(iso + "T00:00:00");
  return [d.getFullYear(), d.getMonth(), d.getDate()];
}

export function tupleFromDate(d: Date): [number, number, number] {
  return [d.getFullYear(), d.getMonth(), d.getDate()];
}

// Inverse of pctOfWindow — where on the calendar is this % of the axis?
export function dateFromPct(pct: number, win: TimeWindow): Date {
  const t = win.start.getTime() + (pct / 100) * (win.end.getTime() - win.start.getTime());
  const d = new Date(t);
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

// Nearest Monday — drag interactions snap to weeks (a single day is ~0.3% of
// the FY axis, far too fine for a pointer); exact dates go through form fields.
export function snapToWeekStart(d: Date): Date {
  const out = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const sinceMonday = (out.getDay() + 6) % 7;
  out.setDate(out.getDate() - sinceMonday + (sinceMonday > 3 ? 7 : 0));
  return out;
}

// Calendar bounds of a quarter id — bar-placement fallback for initiatives
// that carry only a quarter (legacy rows with no exact date).
export function quarterBounds(qId: string): { start: Date; end: Date } | undefined {
  const q = QUARTERS.find(x => x.id === normalizeQuarterId(qId));
  return q ? { start: q.start, end: q.end } : undefined;
}

export function fmtDate(d: Date): string {
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

export function fmtDateLong(d: Date): string {
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

export function makeInitials(name: string): string {
  return name
    .split(/\s+/)
    .map(s => s[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();
}

export function colorForIndex(i: number): string {
  return AVATAR_COLORS[i % AVATAR_COLORS.length];
}
